import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime, DurationLike } from 'luxon';
import { debounceTime, interval, Subject, Subscription } from 'rxjs';
import { BasketService } from '../basket/basket.service';
import { CustomerDetailService } from '../checkout/customer-details.service';
import { KioskService } from '../kiosk/kiosk.service';
import { ProductService } from '../product';
import { ShippingService } from '../shipping';
import { CheckoutService } from '../checkout/checkout.service';

const RESET_KEY = 'store-reset';
const RESET_VALUE = 'kagemand-07';
const TTL_KEY = 'nxt-ttl';

@Injectable({ providedIn: 'root' })
export class ResetService implements OnDestroy {
  private subscription = new Subscription();
  private basketService = inject(BasketService);
  private shippingService = inject(ShippingService);
  private customerDetailService = inject(CustomerDetailService);
  private productService = inject(ProductService);
  private kioskService = inject(KioskService);
  private checkoutService = inject(CheckoutService);
  private router = inject(Router);
  private ngZone = inject(NgZone);

  private readonly daysUntilReset = 14;
  private readonly kioskMinutesUntilReset = 10;
  private readonly checkFrequency = 5 * 60 * 1000;
  private readonly kioskCheckFrequency = 1 * 60 * 1000;

  private lastActive: DateTime;
  private checkActivityQueue = new Subject<void>();
  private dailyResetTimeout: any;

  constructor() {
    if (typeof window === 'undefined') return;
    const localResetValue = localStorage.getItem(RESET_KEY);
    if (localResetValue && localResetValue !== RESET_VALUE) {
      this.reset({ noRedirect: true });
    }
    localStorage.setItem(RESET_KEY, RESET_VALUE);

    const ttl = localStorage.getItem(TTL_KEY);
    this.lastActive = ttl ? DateTime.fromISO(ttl) : DateTime.now();
    this.checkTTL();
    this.setupActivityDetection();

    if (this.kioskService.isKiosk) {
      this.ngZone.runOutsideAngular(() => {
        const now = DateTime.now().toMillis();
        const nextReset = DateTime.now().set({ hour: 1, minute: 0 }).plus({ days: 1 }).toMillis();
        this.dailyResetTimeout = setTimeout(() => {
          this.router.navigate(['/'], { queryParams: { kiosk: 'true' } });

          sessionStorage.clear();
          localStorage.clear();
          localStorage.setItem('kiosk', 'true');

          window.location.reload();
        }, nextReset - now);
      });
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    clearTimeout(this.dailyResetTimeout);
  }

  reset(params: { noRedirect: boolean } = undefined) {
    this.basketService.resetBasket(false);
    this.customerDetailService.resetCustomerDetails(true);
    this.productService.resetProducts();
    this.checkoutService.doneSubmitting();
    sessionStorage.clear();

    if (this.kioskService.useKioskShipping) {
      this.shippingService.loadKioskShipping();
    } else {
      this.shippingService.resetShipping(false);
    }

    this.lastActive = DateTime.now();
    localStorage.setItem(TTL_KEY, this.lastActive.toISO());

    if (!params?.noRedirect) {
      this.router.navigate(['/']);
    }
  }

  checkTTL() {
    const now = DateTime.now();
    const resetDiff: DurationLike = this.kioskService.isKiosk ? { minutes: this.kioskMinutesUntilReset } : { days: this.daysUntilReset };
    if (now > this.lastActive.plus(resetDiff)) {
      this.reset();
    }
  }

  setupActivityDetection() {
    this.ngZone.runOutsideAngular(() => {
      const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart'];
      activityEvents.forEach((event) => {
        document.addEventListener(
          event,
          () => {
            this.checkActivityQueue.next();
          },
          true,
        );
      });

      this.subscription.add(
        this.checkActivityQueue.pipe(debounceTime(1000)).subscribe(() => {
          this.lastActive = DateTime.now();
          localStorage.setItem(TTL_KEY, this.lastActive.toISO());
        }),
      );
      this.subscription.add(
        interval(this.kioskService.isKiosk ? this.kioskCheckFrequency : this.checkFrequency).subscribe(() => this.checkTTL()),
      );
    });
  }
}
